import { api } from "./api";
import type { Renditebruecke } from "./otterfolio";

export type InvestmentKind =
  | "AKTIE"
  | "ETF"
  | "KRYPTO"
  | "ANLEIHE"
  | "ROHSTOFFE"
  | "P2P"
  | "BETEILIGUNG"
  | "SONSTIGE";

/**
 * Kinds die das saldobasierte Modell (Cashflows + latestQuote-Saldo) nutzen
 * statt des transaktionalen Modells (Kauf/Verkauf/Dividende). P2P-Kredite
 * und BETEILIGUNG (Windrad, Buergerwind, Genossenschaft, Crowd) teilen
 * diese Mechanik.
 */
export const BALANCE_INVESTMENT_KINDS: InvestmentKind[] = ["P2P", "BETEILIGUNG"];
export function isBalanceInvestmentKind(kind: InvestmentKind): boolean {
  return BALANCE_INVESTMENT_KINDS.includes(kind);
}
export type Portfolio = "PRIVATE" | "OTTERFOLIO";
export type InvestmentCategory = "FELS" | "RAKETE";
export type QuoteSource = "AUTO" | "MANUAL";
export type InvestmentCashflowType = "DEPOSIT" | "WITHDRAW" | "INTEREST";
export type InvestmentCashflowSource = "IMPORT" | "MANUAL";
export type InvestmentTransactionType = "BUY" | "SELL" | "DIVIDEND" | "TRANSFER";
export type InvestmentTransactionSource = "IMPORT" | "MANUAL";

export interface Quote {
  id: string;
  asOf: string;
  priceCents: number;
  currency: string;
  source: QuoteSource;
  provider: string | null;
  createdAt: string;
}

export interface PositionCalc {
  marketValueCents: number | null;
  marketValueEur: number | null;
  purchaseValueCents: number;
  purchaseValueEur: number | null;
  /** @deprecated Mixed-Currency wenn quoteFxRate ≠ amountFxRate — gainLossEur nutzen. */
  gainLossCents: number | null;
  gainLossPercent: number | null;
  /** marketValueEur - purchaseValueEur (korrekt auch bei Foreign-Quote/EUR-Investment). */
  gainLossEur?: number | null;
  dailyChangeCents: number | null;
  dailyChangePercent: number | null;
  /** dailyChangeCents × quoteFxRate. */
  dailyChangeEur?: number | null;
  stale: boolean;
  investedCents: number | null;
  realizedGainCents?: number | null;
  /** realizedGainCents × amountFxRate. */
  realizedGainEur?: number | null;
  dividendsNetCents?: number | null;
  /** dividendsNetCents × amountFxRate. */
  dividendsNetEur?: number | null;
  totalReturnCents?: number | null;
  /** marketValueEur - purchaseValueEur + realizedGainEur + dividendsNetEur. */
  totalReturnEur?: number | null;
}

export interface CashflowDto {
  id: string;
  date: string;
  type: InvestmentCashflowType;
  amountCents: number;
  source: InvestmentCashflowSource;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddCashflowInput {
  date: string;
  type: InvestmentCashflowType;
  amountCents: number;
  notes?: string;
}

export interface UpdateCashflowInput {
  date?: string;
  type?: InvestmentCashflowType;
  amountCents?: number;
  notes?: string | null;
}

export interface TransactionDto {
  id: string;
  date: string;
  time: string | null;
  type: InvestmentTransactionType;
  shares: string | null;
  priceCents: number | null;
  amountCents: number;
  feeCents: number;
  taxCents: number;
  currency: string;
  externalRef: string | null;
  source: InvestmentTransactionSource;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddTransactionInput {
  date: string;
  time?: string;
  type: InvestmentTransactionType;
  shares?: string | null;
  priceCents?: number | null;
  amountCents: number;
  feeCents: number;
  taxCents: number;
  notes?: string;
}

export interface UpdateTransactionInput {
  date?: string;
  time?: string | null;
  type?: InvestmentTransactionType;
  shares?: string | null;
  priceCents?: number | null;
  amountCents?: number;
  feeCents?: number;
  taxCents?: number;
  notes?: string | null;
}

export interface PeriodReturnDto {
  realizedCents: number;
  realizedEur: number | null;
  dividendsNetCents: number;
  dividendsNetEur: number | null;
  unrealizedDeltaCents: number;
  unrealizedDeltaEur: number | null;
  totalCents: number;
  totalEur: number | null;
  totalPercent: number | null;
  twrPercent: number | null;
  mvStartCents: number | null;
  mvStartEur: number | null;
  basisEur: number | null;
  mvEndCents: number | null;
  mvEndEur: number | null;
  hasData: boolean;
  periodStartIso: string;
  periodEndIso: string;
  /**
   * Realisierte G/V innerhalb der Periode. Cents trotz "Eur"-Suffix — konsistent
   * zu mvEndEur/totalEur. Wert ist cents × amountFx, gerundet. Mit formatEur() nutzbar.
   */
  realizedYtdEur: number;
  /** Lifetime realisierte G/V aus Verkaeufen, cents-skaliert via FX (siehe realizedYtdEur). */
  realizedLifetimeEur: number;
  /** Quelle des mvStart fuer Periode (Quote → in-period-cost → avg-cost → none). */
  startSource: "quote" | "in-period-cost" | "avg-cost" | "none";
}

export interface BenchmarkResult {
  id: string;
  symbol: string;
  displayName: string;
  twrPercent: number | null;
  status:
    | "ok"
    | "pending_backfill"
    | "backfill_failed"
    | "no_quotes_for_period"
    | "fx_failure"
    | "stale";
  statusDetail?: string;
}

export interface CategoryReturnDto {
  kind: "AKTIE" | "ETF" | "KRYPTO" | "P2P" | "BETEILIGUNG" | "SONSTIGE";
  wertaenderungEur: number;
  percent: number | null;
  positionCount: number;
  positionsWithMeasurableBasis: number;
}

export interface PeriodOptions {
  years: number[];
}

export interface BackfillResult {
  investmentId: string;
  name: string;
  status: "ok" | "skipped" | "failed";
  reason?: string;
  quotesCreated?: number;
  fromDate?: string;
  toDate?: string;
}

export interface BackfillSummary {
  total: number;
  ok: number;
  skipped: number;
  failed: number;
  results: BackfillResult[];
}

/**
 * Wenn der Server fuer Refresh/Backfill den GitHub-Actions-Workflow nutzt
 * (Yahoo blockt Hetzner), kommt diese Antwort statt der inline-Summary.
 */
export interface WorkflowTriggerResponse {
  mode: "workflow";
  triggered: true;
  workflow: string;
  runsUrl: string;
  message: string;
}

export type RefreshAllResponse = ({ mode: "inline" } & RefreshAllResult) | WorkflowTriggerResponse;

export type BackfillResponse = ({ mode: "inline" } & BackfillSummary) | WorkflowTriggerResponse;

export interface InvestmentListItem {
  id: string;
  name: string;
  symbol: string;
  kind: InvestmentKind;
  currency: string;
  quantity: string;
  purchasePriceCents: number;
  purchaseDate: string;
  accountId: string | null;
  account: { id: string; name: string; isActive: boolean } | null;
  isActive: boolean;
  notes: string | null;
  isin: string | null;
  isTransactional: boolean;
  portfolio: Portfolio;
  investmentCategory: InvestmentCategory | null;
  growthEstimatePercent: number | null;
  payoutYieldPercent: number | null;
  valuationDeltaPercent: number | null;
  renditebrueckeUpdatedAt: string | null;
  latestQuote: (Quote & { stale: boolean }) | null;
  calc: PositionCalc;
  periodReturn: PeriodReturnDto | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvestmentDetail extends InvestmentListItem {
  quoteCount: number;
  renditebruecke: Renditebruecke | null;
}

export interface RenditebrueckeOverridesInput {
  growthEstimatePercent: number | null;
  payoutYieldPercent: number | null;
  valuationDeltaPercent: number | null;
}

export type QuoteRangeKey = "1M" | "3M" | "6M" | "1J" | "2J" | "3J" | "5J" | "YTD" | "MAX";

export interface InvestmentsSummary {
  totalMarketValueEur: number;
  totalPurchaseValueEur: number;
  totalGainLossEur: number;
  totalGainLossPercent: number | null;
  dailyChangeEur: number;
  dailyChangePercent: number | null;
  positionCount: number;
  lastRefresh: string | null;
  hasStale: boolean;

  periodLabel: string | null;
  periodStartIso: string | null;
  periodEndIso: string | null;
  periodPositionsWithMeasurableBasis: number | null;
  periodPositionCount: number | null;

  periodWertaenderungEur: number | null;
  periodWertaenderungBreakdown: {
    unrealizedEur: number;
    realizedEur: number;
    dividendsNetEur: number;
  } | null;
  periodMvStartEur: number | null;
  periodMvEndEur: number | null;
  periodTwrPercent: number | null;
  periodBenchmarks: BenchmarkResult[];

  periodCategoryReturns: CategoryReturnDto[] | null;

  periodXirrPercent: number | null;
}

export interface RefreshAllResult {
  updated: string[];
  failed: { id: string; error: { code: string; message: string } }[];
  fxFetches: { currency: string; ok: boolean }[];
}

export interface CreateInvestmentInput {
  name: string;
  symbol: string;
  kind: InvestmentKind;
  currency: "EUR" | "USD" | "GBP" | "CHF" | "JPY";
  quantity: string;
  purchasePriceCents: number;
  purchaseDate: string;
  accountId?: string;
  notes?: string;
  isin?: string | null;
  portfolio?: Portfolio;
  investmentCategory?: InvestmentCategory | null;
}

export type UpdateInvestmentInput = Partial<{
  name: string;
  symbol: string;
  kind: InvestmentKind;
  quantity: string;
  purchasePriceCents: number;
  purchaseDate: string;
  accountId: string | null;
  notes: string | null;
  isActive: boolean;
  isin: string | null;
  portfolio: Portfolio;
  investmentCategory: InvestmentCategory | null;
  growthEstimatePercent: number | null;
  payoutYieldPercent: number | null;
  valuationDeltaPercent: number | null;
}>;

export interface CreateManualQuoteInput {
  asOf: string;
  priceCents: number;
  currency: "EUR" | "USD" | "GBP" | "CHF" | "JPY";
}

export interface ListInvestmentsFilters {
  active?: boolean;
  kind?: InvestmentKind;
  accountId?: string;
  period?: string;
}

function qs(filters: ListInvestmentsFilters): string {
  const sp = new URLSearchParams();
  if (filters.active !== undefined) sp.set("active", String(filters.active));
  if (filters.kind) sp.set("kind", filters.kind);
  if (filters.accountId) sp.set("accountId", filters.accountId);
  if (filters.period) sp.set("period", filters.period);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const investmentsApi = {
  list: (filters: ListInvestmentsFilters = {}) =>
    api<{ investments: InvestmentListItem[] }>(`/investments${qs(filters)}`),
  summary: (period?: string) =>
    api<InvestmentsSummary>(`/investments/summary${period ? `?period=${period}` : ""}`),
  get: (id: string, period?: string) =>
    api<InvestmentDetail>(`/investments/${id}${period ? `?period=${period}` : ""}`),
  listQuotes: (id: string, range: QuoteRangeKey = "MAX") =>
    api<{ quotes: Quote[] }>(`/investments/${id}/quotes?range=${range}`),
  periodOptions: () => api<PeriodOptions>("/investments/period-options"),
  backfillQuotes: (params: { investmentIds?: string[]; mode?: "incremental" | "full" } = {}) =>
    api<BackfillResponse>("/investments/backfill-quotes", {
      method: "POST",
      body: params,
    }),
  create: (input: CreateInvestmentInput) =>
    api<InvestmentDetail>("/investments", { method: "POST", body: input }),
  update: (id: string, input: UpdateInvestmentInput) =>
    api<InvestmentDetail>(`/investments/${id}`, { method: "PATCH", body: input }),
  updateRenditebrueckeOverrides: (id: string, input: RenditebrueckeOverridesInput) =>
    api<InvestmentDetail>(`/investments/${id}/renditebruecke-overrides`, {
      method: "PATCH",
      body: input,
    }),
  softDelete: (id: string) => api<void>(`/investments/${id}`, { method: "DELETE" }),
  hardDelete: (id: string) => api<void>(`/investments/${id}?force=true`, { method: "DELETE" }),
  addManualQuote: (id: string, input: CreateManualQuoteInput) =>
    api<Quote>(`/investments/${id}/quotes`, { method: "POST", body: input }),
  deleteQuote: (id: string, quoteId: string) =>
    api<void>(`/investments/${id}/quotes/${quoteId}`, { method: "DELETE" }),
  refresh: (id: string) => api<Quote>(`/investments/${id}/refresh`, { method: "POST" }),
  refreshAll: () => api<RefreshAllResponse>("/investments/refresh-all", { method: "POST" }),
  listCashflows: (investmentId: string) =>
    api<{ cashflows: CashflowDto[] }>(`/investments/${investmentId}/cashflows`),
  addCashflow: (investmentId: string, input: AddCashflowInput) =>
    api<CashflowDto>(`/investments/${investmentId}/cashflows`, { method: "POST", body: input }),
  updateCashflow: (investmentId: string, cashflowId: string, patch: UpdateCashflowInput) =>
    api<CashflowDto>(`/investments/${investmentId}/cashflows/${cashflowId}`, {
      method: "PATCH",
      body: patch,
    }),
  deleteCashflow: (investmentId: string, cashflowId: string) =>
    api<void>(`/investments/${investmentId}/cashflows/${cashflowId}`, { method: "DELETE" }),
  listTransactions: (investmentId: string) =>
    api<{ transactions: TransactionDto[] }>(`/investments/${investmentId}/transactions`),
  addTransaction: (investmentId: string, input: AddTransactionInput) =>
    api<TransactionDto>(`/investments/${investmentId}/transactions`, {
      method: "POST",
      body: input,
    }),
  updateTransaction: (investmentId: string, transactionId: string, patch: UpdateTransactionInput) =>
    api<TransactionDto>(`/investments/${investmentId}/transactions/${transactionId}`, {
      method: "PATCH",
      body: patch,
    }),
  deleteTransaction: (investmentId: string, transactionId: string) =>
    api<void>(`/investments/${investmentId}/transactions/${transactionId}`, { method: "DELETE" }),
  // Country Weights
  getCountryWeights: (id: string) =>
    api<CountryWeightsResponse>(`/investments/${id}/country-weights`),
  setCountryWeights: (id: string, weights: CountryWeight[]) =>
    api<CountryWeightsMutationResponse>(`/investments/${id}/country-weights`, {
      method: "PUT",
      body: { weights },
    }),
  autoLookupCountryWeights: (id: string) =>
    api<CountryWeightsLookupResponse>(`/investments/${id}/country-weights/lookup`, {
      method: "POST",
    }),
  deleteCountryWeights: (id: string) =>
    api<void>(`/investments/${id}/country-weights`, { method: "DELETE" }),
};

export interface CountryWeight {
  country: string;
  weightBp: number;
}

export type CountryWeightSource = "MANUAL" | "ISIN" | "SEED";

export interface CountryWeightsResponse {
  weights: CountryWeight[] | null;
  source: CountryWeightSource | null;
  updatedAt: string | null;
  supported: boolean;
}

export interface CountryWeightsMutationResponse {
  weights: CountryWeight[];
  source: CountryWeightSource;
  updatedAt: string;
}

export interface CountryWeightsLookupResponse {
  weights: CountryWeight[] | null;
  source: CountryWeightSource | null;
  updatedAt: string | null;
  matched: boolean;
}
